const CUSTOM_SORT_PARAMETER = "rr_sort";
const VALID_DIRECTIONS = new Set(["ascending", "descending"]);
const loadingLists = new WeakSet();

function pageCount(root) {
  return Array.from(root.querySelectorAll(".pagination a[href]"), (link) => {
    return Number.parseInt(new URL(link.href, window.location.origin).searchParams.get("page") || "1", 10);
  }).reduce((maximum, page) => Math.max(maximum, page), 1);
}

function cardWeight(card) {
  const weight = Number.parseFloat(card.dataset.rrCaratWeight || "0");
  return Number.isFinite(weight) ? weight : 0;
}

function alphabeticalKey(card) {
  return (card.querySelector('.product-title')?.textContent || card.getAttribute('handle') || '')
    .trim().replace(/^the\s+/i, '');
}

function compareTitles(left, right, direction) {
  const difference = alphabeticalKey(left).localeCompare(alphabeticalKey(right), 'en', { sensitivity: 'base' });
  const tie = (left.getAttribute('handle') || '').localeCompare(right.getAttribute('handle') || '');
  return (direction === 'descending' ? -1 : 1) * (difference || tie);
}

function updateCaratControl(section, direction) {
  const row = section.querySelector('[data-rr-sort-control-row="carat"]');
  const labelToggle = row?.querySelector("[data-rr-sort-label-toggle]");
  const directionControls = Array.from(row?.querySelectorAll("[data-rr-sort-direction-action]") || []);
  if (!row || !labelToggle || directionControls.length === 0) return;

  const isActive = VALID_DIRECTIONS.has(direction);
  row.classList.toggle("is-active", isActive);
  labelToggle.setAttribute("aria-selected", String(isActive));

  if (isActive) {
    row.dataset.rrSortDirection = direction;
  } else {
    delete row.dataset.rrSortDirection;
  }

  const nextDirection = direction === "ascending" ? "descending" : "ascending";
  const currentDirectionLabel = direction === "ascending" ? "low to high" : "high to low";
  const nextDirectionLabel = nextDirection === "ascending" ? "low to high" : "high to low";
  labelToggle.value = `rr-carat-${isActive ? nextDirection : "ascending"}`;
  labelToggle.setAttribute("aria-label", isActive ? `Carats are sorted ${currentDirectionLabel}. Activate to sort ${nextDirectionLabel}` : "Sort Carats, low to high");

  directionControls.forEach((control) => {
    const controlDirection = control.dataset.rrSortDirectionChoice;
    control.setAttribute("aria-pressed", String(isActive && controlDirection === direction));
  });
}

// A controller belongs to one results list. Replacement invalidates all its work.
const collectionStreams = new Map();
function resultKey(url) {
  const key = window.RRCatalogAvailability?.effectiveURL(url) || new URL(url, window.location.origin);
  key.searchParams.delete('page');
  key.searchParams.delete('section_id');
  key.searchParams.sort();
  return key.pathname + key.search;
}

class CollectionStream {
  constructor(section, productList) {
    this.section = section;
    this.list = productList;
    this.url = (window.RRCatalogAvailability?.effectiveURL(window.location.href) || new URL(window.location.href));
    this.key = resultKey(this.url);
    this.page = Number(productList.dataset.rrPage || 1);
    this.size = Number(productList.dataset.rrPageSize || 18);
    this.pages = Number(productList.dataset.rrPages || pageCount(section));
    this.request = new AbortController();
    this.seen = new Set(Array.from(productList.querySelectorAll(':scope > product-card'), card => card.getAttribute('handle')));
    this.pagination = section.querySelector('.collection__main > .pagination');
    this.pending = null;
    this.rowBuffer = [];
    this.busy = false;
    this.prefetched = null;
    for (const node of Array.from(productList.children || [])) node.dataset.rrSourcePage = String(this.page);
  }
  current() {
    return !this.request.signal.aborted && this.list.isConnected && resultKey(window.location.href) === this.key;
  }
  destroy() {
    this.request.abort();
    this.prefetched = null;
    document.removeEventListener?.('rr:grid-density-change', this.onDensityChange);
    window.removeEventListener?.('load', this.onInitialLoad);
    this.observer?.disconnect();
    window.removeEventListener?.('scroll', this.onScroll);
    this.control?.remove();
  }
  columns() {
    const value = Number(document.documentElement?.dataset.rrGridDensity);
    return window.matchMedia?.('(min-width: 1000px)').matches && value >= 1 && value <= 6 ? value : 1;
  }
  batchSize() {
    const columns = this.columns();
    return Math.max(columns, Math.floor(this.size / columns) * columns);
  }
  alignRows() {
    if (this.busy || this.returnFill || this.restoring || window.rrCollectionReturnPosition) return;
    const more = this.pending ? this.pending.length : this.rowBuffer.length || this.page < this.pages;
    if (!more) return;
    const cards = Array.from(this.list.querySelectorAll(':scope > product-card'));
    const remainder = cards.length % this.columns();
    if (!remainder) return;
    const tail = cards.slice(-remainder);
    for (const card of tail) { this.seen.delete(card.getAttribute('handle')); card.remove(); }
    if (this.pending) this.pending.unshift(...tail);
    else this.rowBuffer.unshift(...tail);
    document.dispatchEvent(new CustomEvent('rr:collection-cards-sorted', {detail: {root: this.section}}));
  }
  mount() {
    this.alignRows();
    this.list.addEventListener('rr:detail-needs-more', () => {
      if (this.pending?.length || this.rowBuffer.length || this.page < this.pages) this.load();
    }, {signal: this.request.signal});
    this.onDensityChange = () => this.alignRows();
    document.addEventListener('rr:grid-density-change', this.onDensityChange, {signal: this.request.signal});
    if (!this.current() || !(this.pending ? this.pending.length : this.rowBuffer.length || this.page < this.pages)) return;
    this.control = document.createElement('div');
    this.control.className = 'rr-collection-more';
    this.control.style.cssText = 'display:grid;justify-items:center;gap:12px;padding:32px 0;';
    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.className = 'button button--outline';
    this.button.textContent = 'Load more';
    this.button.setAttribute('aria-controls', this.list.id);
    this.status = document.createElement('span');
    this.status.setAttribute('role', 'status');
    this.status.className = 'text-sm text-subdued';
    this.control.append(this.button, this.status);
    this.list.after(this.control);
    if (this.pagination) this.pagination.hidden = true;
    this.button.addEventListener('click', () => this.returnFill ? this.fillSavedBatches() : this.load());
    // Fast wheel/touch jumps can cross the sentinel between observer samples.
    this.onScroll = () => {
      if (this.frame || this.returnFill || window.rrCollectionReturnPosition) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        if (this.control?.isConnected && this.control.getBoundingClientRect().top < window.innerHeight + 1500) this.load();
      });
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(entries => {
        if (!this.returnFill && !window.rrCollectionReturnPosition && entries.some(entry => entry.isIntersecting)) this.load();
      }, { rootMargin: '0px 0px 1500px 0px' });
      this.observer.observe(this.control);
    }
    this.onInitialLoad = () => { if (!this.busy) this.prefetch(); };
    if (document.readyState === 'complete') this.onInitialLoad();
    else window.addEventListener('load', this.onInitialLoad, { once: true });
  }
  // Keep one detached results page ready; never append or chain on completion.
  prefetch() {
    if (window.rrCollectionReturnPosition || this.returnFill || this.restoring || this.pending || this.prefetched || !this.current() || this.page >= this.pages) return;
    const entry = { page: this.page + 1, promise: null };
    this.prefetched = entry;
    entry.promise = fetchPage(this.url, this.section.id.replace('shopify-section-', ''), entry.page, this.request.signal, 'low')
      .then(page => this.current() ? page : null)
      .catch(() => null);
  }
  async restoreVisibleBatch(saved) {
    const target = saved.anchorPage;
    const batch = saved.batches?.find(item => item.page === target);
    const last = saved.batches?.at(-1)?.page;
    if (this.list.classList.contains('rr-detail-slides') || saved.rowBatches || this.rowBuffer.length || this.returnFill || this.pending || this.busy || !batch || !Number.isInteger(target) || target < this.page
      || last > this.pages || saved.startPage !== this.page || !saved.batches.every(item => item.columns?.length)
      || Math.abs(this.list.getBoundingClientRect().width - saved.width) > 1
      || saved.layout !== this.layoutKey()
      || this.list.querySelectorAll(':scope > product-card').length >= saved.count) return false;
    try {
      // Cover the whole saved viewport before revealing, including a batch boundary.
      const visibleEnd = Math.max(target, ...saved.batches.filter(item => item.top < saved.viewportTop + window.innerHeight + 200).map(item => item.page));
      const foreground = [];
      for (let number = target; number <= visibleEnd; number++) {
        const page = number === this.page ? null : await fetchPage(this.url, this.section.id.replace('shopify-section-', ''), number, this.request.signal, 'high');
        if (!this.current()) return false;
        const nodes = Array.from((page ? page.querySelector('product-list') : this.list)?.children || []);
        if (!nodes.some(node => node.matches('product-card'))) return false;
        foreground.push({number, nodes});
      }
      if (!foreground[0].nodes.some(node => node.getAttribute('handle') === saved.anchor)) return false;
      const gap = () => {
        const node = document.createElement('div');
        node.setAttribute('aria-hidden', 'true');
        node.dataset.rrReturnGap = '';
        node.style.cssText = 'grid-column:1/-1;grid-row:auto;display:block;height:0px;min-height:0;pointer-events:none;';
        return node;
      };
      const before = target > this.page + 1 ? gap() : null;
      const after = last > visibleEnd ? gap() : null;
      const oldOverflowAnchor = this.list.style.overflowAnchor;
      this.list.style.overflowAnchor = 'none';
      this.returnColumns = new Map();
      this.prepareReturnNodes(Array.from(this.list.children), this.page, saved);
      if (before) this.list.append(before);
      for (const {number, nodes} of foreground) {
        this.prepareReturnNodes(nodes, number, saved);
        for (const node of nodes) if (node.matches('product-card')) this.seen.add(node.getAttribute('handle'));
        if (number !== this.page) this.list.append(...nodes);
      }
      if (after) this.list.append(after);
      const top = foreground[0].nodes[0].getBoundingClientRect().top - this.list.getBoundingClientRect().top;
      if (before) before.style.height = `${Math.max(0, batch.top - top)}px`;
      if (after) after.style.height = `${Math.max(0, saved.height - this.list.getBoundingClientRect().height)}px`;
      this.returnFill = {saved, target, before, after, oldOverflowAnchor,
        queue: Array.from({length: last - this.page}, (_, index) => this.page + index + 1).filter(page => page < target || page > visibleEnd)};
      document.dispatchEvent(new CustomEvent('rr:collection-cards-sorted', {detail: {root: this.section}}));
      return true;
    } catch (error) {
      if (error.name !== 'AbortError') console.warn('Collection visible batch could not be restored; using sequential restoration.', error);
      return false;
    }
  }
  prepareReturnNodes(nodes, number, saved) {
    const columns = saved.batches.find(item => item.page === number)?.columns || [];
    nodes.forEach((node, index) => {
      node.dataset.rrSourcePage = String(number);
      // Gap elements must not change nth-child based last-row centering.
      if (!this.returnColumns.has(node)) this.returnColumns.set(node, node.style.gridColumn || '');
      node.style.gridColumn = columns[index] || 'auto';
      this.settleReturnCard(node);
      node.querySelectorAll('img').forEach(img => { img.loading = 'lazy'; img.setAttribute('fetchpriority', 'low'); });
    });
  }
  settleReturnCard(node) {
    node.removeAttribute('reveal-on-scroll');
    node.dataset.rrProductRevealReady = 'true';
    node.classList?.remove('rr-product-card-reveal-pending', 'rr-product-card-reveal-active');
    node.style.animation = 'none';
    node.style.translate = 'none';
    node.getAnimations?.().forEach(animation => animation.cancel());
    node.style.opacity = '1';
    node.style.transform = 'none';
  }
  async prepareVisibleImages() {
    const images = [];
    for (const node of this.list.children) {
      if (node.dataset.rrReturnGap !== undefined) continue;
      this.settleReturnCard(node);
      const rect = node.getBoundingClientRect();
      if (rect.bottom <= 0 || rect.top >= window.innerHeight + 200) continue;
      for (const image of node.querySelectorAll('img')) {
        image.loading = 'eager';
        image.setAttribute('fetchpriority', 'high');
        images.push(waitForReturnImage(image, this.request.signal));
      }
    }
    await Promise.all(images);
  }
  layoutKey() {
    return `${this.list.getAttribute('collection-mobile-layout')}:${this.list.getAttribute('collection-desktop-layout')}:${getComputedStyle(this.list).gridTemplateColumns}`;
  }
  preserveVisiblePosition(change) {
    const anchor = Array.from(this.list.querySelectorAll(':scope > product-card')).find(card => card.getBoundingClientRect().bottom > 0);
    const top = anchor?.getBoundingClientRect().top;
    change();
    if (anchor?.isConnected) {
      const difference = anchor.getBoundingClientRect().top - top;
      if (Math.abs(difference) > 0.5) window.scrollBy({top: difference, behavior: 'instant'});
    }
  }
  async fillSavedBatches() {
    if (!this.returnFill || this.busy || !this.current()) return;
    this.busy = true;
    this.button.disabled = true;
    this.status.hidden = true;
    const state = this.returnFill;
    try {
      while (state.queue.length && this.current()) {
        const number = state.queue[0];
        const page = await fetchPage(this.url, this.section.id.replace('shopify-section-', ''), number, this.request.signal, 'low');
        if (!this.current()) return;
        const nodes = Array.from(page.querySelector('product-list')?.children || []);
        if (!nodes.some(node => node.matches('product-card'))) throw new Error('Missing collection results');
        const additions = nodes.filter(node => !node.matches('product-card') || !this.seen.has(node.getAttribute('handle')));
        this.prepareReturnNodes(additions, number, state.saved);
        for (const node of additions) {
          node.dataset.rrSourcePage = String(number);
          if (node.matches('product-card')) this.seen.add(node.getAttribute('handle'));
          node.querySelectorAll('img').forEach(img => { img.loading = 'lazy'; img.setAttribute('fetchpriority', 'low'); });
        }
        this.preserveVisiblePosition(() => {
          const gap = number < state.target ? state.before : state.after;
          const oldHeight = this.list.getBoundingClientRect().height;
          for (const node of additions) this.list.insertBefore(node, gap);
          const growth = this.list.getBoundingClientRect().height - oldHeight;
          gap.style.height = `${Math.max(0, parseFloat(gap.style.height) - growth)}px`;
          state.queue.shift();
          if (!state.queue.some(page => (page < state.target) === (number < state.target))) gap.remove();
        });
        document.dispatchEvent(new CustomEvent('rr:collection-cards-sorted', {detail: {root: this.section}}));
      }
      if (!this.current()) return;
      this.page = state.saved.batches.at(-1).page;
      this.preserveVisiblePosition(() => {
        for (const [node, column] of this.returnColumns) node.style.gridColumn = column;
        this.returnColumns.clear();
      });
      this.returnFill = null;
      this.list.style.overflowAnchor = state.oldOverflowAnchor;
      if (this.page >= this.pages) {
        this.observer?.disconnect();
        window.removeEventListener?.('scroll', this.onScroll);
        this.control?.remove();
        this.button.hidden = true;
      }
      this.prefetch();
    } catch (error) {
      if (error.name !== 'AbortError' && this.current()) {
        this.status.hidden = false;
        this.status.textContent = 'Unable to load more pieces. Please try again.';
      }
    } finally {
      this.busy = false;
      this.button.disabled = false;
    }
  }
  async restore(count) {
    this.restoring = true;
    try {
      while (this.current() && !this.busy && this.button && this.list.querySelectorAll(':scope > product-card').length < count) {
        const before = this.list.querySelectorAll(':scope > product-card').length;
        if (this.pending) {
          await this.load();
        } else {
          const remaining = Math.min(3, this.pages - this.page, Math.ceil((count - before) / this.size));
          if (remaining <= 0) break;
          const firstPage = this.page + 1;
          const cached = this.prefetched;
          this.prefetched = null;
          const pages = await Promise.all(Array.from({ length: remaining }, (_, index) => {
            const page = firstPage + index;
            return cached?.page === page ? cached.promise : fetchPage(this.url,
              this.section.id.replace('shopify-section-', ''), page, this.request.signal).catch(() => null);
          }));
          for (let index = 0; index < pages.length; index++) {
            if (!this.current()) return;
            await this.load(pages[index]);
            if (this.page !== firstPage + index) return;
          }
        }
        if (this.list.querySelectorAll(':scope > product-card').length === before) break;
      }
    } finally {
      this.restoring = false;
    }
    this.prefetch();
  }
  async restoreAnchor(saved) {
    const cards = Array.from(this.list.querySelectorAll(':scope > product-card'));
    if (cards.some(card => card.getAttribute('handle') === saved.anchor)) return;
    // A changed density invalidates saved geometry, not the anchor's page.
    // Only fetch through that page before revealing; later pages load normally.
    const requiredCount = Number.isInteger(saved.anchorPage) && saved.anchorPage >= this.page
      ? Math.min(saved.count, cards.length + (saved.anchorPage - this.page) * this.size)
      : saved.count;
    await this.restore(requiredCount);
  }
  async load(restoredPage = null) {
    if (this.returnFill || this.busy || !this.current()) return;
    this.busy = true;
    this.button.disabled = true;
    this.button.innerHTML = '<span class="button__state-content"><span class="button__state-spinner" aria-hidden="true"></span><span>Loading</span></span>';
    this.button.setAttribute('aria-busy', 'true');
    this.status.hidden = true;
    this.status.textContent = '';
    this.list.setAttribute('aria-busy', 'true');
    try {
      let nodes;
      if (this.pending) {
        nodes = this.pending.slice(0, this.batchSize());
      } else if (this.page < this.pages) {
        const cached = this.prefetched;
        let page = restoredPage || (cached?.page === this.page + 1 ? await cached.promise : null);
        this.prefetched = null;
        if (!this.current()) return;
        // Failed speculative work stays silent; foreground loading can retry.
        if (!page) page = await fetchPage(this.url, this.section.id.replace('shopify-section-', ''), this.page + 1, this.request.signal);
        const nextList = page.querySelector('product-list');
        if (!nextList) throw new Error('Missing collection results');
        nodes = Array.from(nextList.children);
        if (!nodes.some(node => node.matches('product-card'))) throw new Error('Empty collection page');
        if (!this.current()) return;
        for (const node of nodes) if (node.dataset) node.dataset.rrSourcePage = String(this.page + 1);
        this.page += 1;
      } else { nodes = []; }
      if (!this.pending) {
        nodes = [...this.rowBuffer, ...nodes];
        this.rowBuffer = [];
      }
      if (!this.current()) return;
      const additions = nodes.filter(node => !node.matches('product-card') || !this.seen.has(node.getAttribute('handle')));
      if (!this.pending && this.page < this.pages) {
        const total = this.list.querySelectorAll(':scope > product-card').length + additions.filter(node => node.matches('product-card')).length;
        let remainder = total % this.columns();
        while (remainder > 0 && additions.length) {
          const node = additions.pop();
          this.rowBuffer.unshift(node);
          if (node.matches('product-card')) remainder -= 1;
        }
      }
      for (const node of additions) {
        if (node.dataset && !node.dataset.rrSourcePage) node.dataset.rrSourcePage = String(this.page);
        if (node.matches('product-card')) this.seen.add(node.getAttribute('handle'));
        // Later batches never compete with initial-page images for priority.
        node.querySelectorAll('img').forEach(img => { img.loading = 'lazy'; img.setAttribute('fetchpriority', 'low'); });
      }
      this.list.append(...additions);
      if (this.pending) this.pending.splice(0, nodes.length);
      document.dispatchEvent(new CustomEvent('rr:collection-cards-sorted', { detail: { root: this.section } }));
      this.status.textContent = '';
      this.status.hidden = true;
      const more = this.pending ? this.pending.length > 0 : this.rowBuffer.length > 0 || this.page < this.pages;
      if (more) this.prefetch();
      if (!more) {
        this.observer?.disconnect();
        window.removeEventListener?.('scroll', this.onScroll);
        this.control?.remove();
        this.button.hidden = true;
      }
    } catch (error) {
      if (error.name !== 'AbortError' && this.current()) {
        this.status.hidden = false;
        this.status.textContent = 'Unable to load more pieces. Please try again.';
        window.removeEventListener?.('scroll', this.onScroll);
        // A failure must require a deliberate retry, not an automatic request loop.
        this.observer?.disconnect();
      }
    } finally {
      this.busy = false;
      this.button.disabled = false;
      this.button.textContent = 'Load more';
      this.button.removeAttribute('aria-busy');
      this.list.removeAttribute('aria-busy');
    }
  }
}

// Search uses the same in-memory batch stream as collection carat/title sorting.
window.RRCollectionStream = CollectionStream;

async function fetchPage(url, sectionId, page, signal, priority = 'auto') {
  const pageUrl = new URL(url);
  pageUrl.searchParams.set("page", String(page));
  pageUrl.searchParams.set("section_id", sectionId);

  const response = await fetch(pageUrl, { signal, priority, headers: { "X-Requested-With": "XMLHttpRequest" } });
  if (!response.ok) throw new Error(`Unable to load collection page ${page}`);

  return new DOMParser().parseFromString(await response.text(), "text/html");
}

async function applyCaratSort(root = document) {
  const direction = (window.RRCatalogAvailability?.effectiveURL(window.location.href) || new URL(window.location.href)).searchParams.get(CUSTOM_SORT_PARAMETER);

  const section = root.matches?.(".shopify-section--main-collection")
    ? root
    : root.closest?.(".shopify-section--main-collection")
      || root.querySelector?.(".shopify-section--main-collection");
  if (!section) return;

  updateCaratControl(section, direction);
  const nativeSort = (window.RRCatalogAvailability?.effectiveURL(window.location.href) || new URL(window.location.href)).searchParams.get('sort_by')
    || section.querySelector('#sort-by-popover [aria-selected="true"]')?.value
    || document.querySelector('rr-collection-drawer-sort')?.dataset.defaultSortBy;
  const titleDirection = /^title-(ascending|descending)$/.exec(nativeSort || '')?.[1];
  const sortTitles = !VALID_DIRECTIONS.has(direction) && !!titleDirection;
  const productList = section.querySelector("product-list");
  if (!productList || loadingLists.has(productList)) return;
  const previous = collectionStreams.get(section.id);
  if (previous?.list === productList) return;
  previous?.destroy();
  const stream = new CollectionStream(section, productList);
  collectionStreams.set(section.id, stream);
  if (!VALID_DIRECTIONS.has(direction) && !sortTitles) {
    stream.mount();
    return;
  }
  loadingLists.add(productList);

  if (!sortTitles) section.querySelectorAll('#sort-by-popover [role="option"]').forEach((option) => {
    if (option.matches('[data-rr-sort-control="carat"][data-rr-sort-label-toggle]')) {
      option.setAttribute("aria-selected", "true");
    } else {
      option.setAttribute("aria-selected", "false");
    }
  });

  section.dataset.rrCaratSortLoading = "true";
  productList.setAttribute("aria-busy", "true");

  try {
    const sectionId = section.id.replace("shopify-section-", "");
    const totalPages = Number(productList.dataset.rrPages) || pageCount(section);
    const pageNumbers = Array.from({ length: totalPages }, (_, index) => index + 1)
      .filter(page => page !== stream.page);
    const pages = [];
    // Bound concurrent requests for large filtered collections.
    for (let offset = 0; offset < pageNumbers.length; offset += 3) {
      if (!stream.current()) return;
      pages.push(...await Promise.all(pageNumbers.slice(offset, offset + 3).map(page =>
        fetchPage(stream.url, sectionId, page, stream.request.signal)
      )));
    }

    if (!productList.isConnected || !stream.current()) return;
    const cards = Array.from(productList.querySelectorAll(":scope > product-card"));
    for (const page of pages) {
      cards.push(...page.querySelectorAll("product-list > product-card"));
    }

    cards.sort((left, right) => {
      if (sortTitles) return compareTitles(left, right, titleDirection);
      const difference = cardWeight(left) - cardWeight(right);
      if (difference !== 0) return direction === "ascending" ? difference : -difference;
      return (left.getAttribute("handle") || "").localeCompare(right.getAttribute("handle") || "");
    });

    const promotions = Array.from(productList.children).filter((child) => !child.matches("product-card"));
    const promotionPositions = promotions.map((promotion) => ({
      promotion,
      position: Array.prototype.indexOf.call(productList.children, promotion)
    }));

    // Preserve reveal observers and animation cleanup attached to existing cards.
    stream.pending = cards.slice(stream.batchSize());
    const initial = cards.slice(0, stream.batchSize());
    stream.seen = new Set(initial.map(card => card.getAttribute('handle')));
    productList.replaceChildren(...initial);
    for (const { promotion, position } of promotionPositions) {
      productList.insertBefore(promotion, productList.children[position] || null);
    }

    if (stream.pagination) stream.pagination.hidden = true;
    stream.mount();
    document.dispatchEvent(new CustomEvent("rr:collection-cards-sorted", { detail: { root: section } }));
  } catch (error) {
    if (error.name !== 'AbortError' && stream.current()) {
      console.error("Raiman Rocks sorting could not load the complete collection.", error);
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'button button--outline';
      retry.textContent = sortTitles ? 'Unable to sort alphabetically. Try again' : 'Unable to sort by carats. Try again';
      productList.before(retry);
      retry.addEventListener('click', () => {
        retry.remove();
        collectionStreams.delete(section.id);
        applyCaratSort(section);
      }, { once: true });
    }
  } finally {
    productList.removeAttribute("aria-busy");
    loadingLists.delete(productList);
    if (productList.isConnected) delete section.dataset.rrCaratSortLoading;
  }
}


// Decode both primary and hover art before a restored viewport fades in.
// Failed images and aborted navigation must never leave the page permanently hidden.
function waitForReturnImage(image, signal) {
  return new Promise(resolve => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      image.removeEventListener('load', loaded);
      image.removeEventListener('error', finish);
      signal.removeEventListener('abort', finish);
      resolve();
    };
    const loaded = () => {
      if (image.naturalWidth && image.decode) image.decode().catch(() => {}).then(finish);
      else finish();
    };
    const timer = setTimeout(finish, 8000);
    image.addEventListener('load', loaded, {once: true});
    image.addEventListener('error', finish, {once: true});
    signal.addEventListener('abort', finish, {once: true});
    if (signal.aborted) finish();
    else if (image.complete) loaded();
  });
}

const returnStorageKey = 'rr-collection-return-position';
let restoringCollection = false;
function rememberCollectionPosition() {
  if (restoringCollection) return;
  const stream = Array.from(collectionStreams.values()).find(item => item.current());
  if (!stream) return;
  const cards = Array.from(stream.list.querySelectorAll(':scope > product-card'));
  const anchor = cards.find(card => card.hasAttribute('data-detail-active') && !card.inert) || cards.find(card => card.getBoundingClientRect().bottom > 0);
  const rect = stream.list.getBoundingClientRect();
  const batches = [];
  for (const node of stream.list.children) {
    const page = Number(node.dataset.rrSourcePage);
    if (!page) continue;
    let batch = batches.find(item => item.page === page);
    if (!batch) batches.push(batch = {page, top: node.getBoundingClientRect().top - rect.top, columns: []});
    batch.columns.push(getComputedStyle(node).gridColumn);
  }
  const geometry = stream.returnFill?.saved || {
    rowBatches: stream.rowBuffer.length > 0, count: cards.length, batches, startPage: Number(stream.list.dataset.rrPage || 1),
    width: rect.width, height: rect.height, layout: stream.layoutKey()
  };
  try { sessionStorage.setItem(returnStorageKey, JSON.stringify({
    ...geometry, viewportTop: -rect.top, url: location.href, time: Date.now(), y: scrollY,
    anchorPage: Number(anchor?.dataset.rrSourcePage),
    anchor: anchor?.getAttribute('handle'), offset: anchor?.getBoundingClientRect().top
  })); } catch {}
}
document.addEventListener('click', event => {
  const link = event.target.closest?.('a[href]');
  if (link && new URL(link.href, location.href).pathname.includes('/products/')) rememberCollectionPosition();
}, true);
window.addEventListener('pagehide', rememberCollectionPosition);
async function restoreCollectionPosition() {
  const saved = window.rrCollectionReturnPosition;
  if (!saved) return;
  restoringCollection = true;
  let stream;
  try {
    stream = Array.from(collectionStreams.values()).find(item => item.current());
    if (!stream) return;
    if (!await stream.restoreVisibleBatch(saved)) await stream.restoreAnchor(saved);
    await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    const anchor = Array.from(stream.list.querySelectorAll(':scope > product-card')).find(card => card.getAttribute('handle') === saved.anchor);
    if (stream.current()) {
      // At the top, card geometry can still change as the drawer and fonts settle.
      // Preserve the absolute top instead of converting those changes into scroll.
      const targetY = saved.y <= 1 ? 0
        : anchor ? scrollY + anchor.getBoundingClientRect().top - saved.offset : saved.y;
      window.scrollTo({top: targetY, behavior:'instant'});
      await stream.prepareVisibleImages();
    }
  } finally {
    restoringCollection = false;
    delete window.rrCollectionReturnPosition;
    stream?.alignRows();
    const wasHidden = document.documentElement.classList.contains('rr-collection-restoring');
    document.documentElement.classList.remove('rr-collection-restoring');
    if (wasHidden && stream?.current() && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      document.getElementById('main')?.animate([{opacity: 0}, {opacity: 1}], {duration: 1000, easing: 'cubic-bezier(0.22, 1, 0.36, 1)'});
    }
    window.dispatchEvent(new Event('scroll'));
    stream?.fillSavedBatches();
  }
}
window.addEventListener('pageshow', event => {
  if (!event.persisted) return;
  try { window.rrCollectionReturnPosition = JSON.parse(sessionStorage.getItem(returnStorageKey) || 'null'); } catch {}
  restoreCollectionPosition();
});

applyCaratSort().then(restoreCollectionPosition);
document.addEventListener("rr:collection-results-replaced", (event) => {
  const work = applyCaratSort(event.detail.root);
  event.detail.waitUntil?.(work);
});
